/**
 * `useFilePresignedUrl` — shared hook for video + file embed NodeViews.
 * Fetches a presigned GET URL for a tenant-bucket attachment row.
 *
 * Lifecycle model: mint once, hold for the duration of the mount, refresh
 * only when the consumer surfaces an error. The BE signs wiki kinds
 * (`wiki-video`, `wiki-file`) for 1h; for an actively-viewed embed the
 * initial URL almost always outlives the reader's session. The rare
 * stale-URL case — a reader who leaves the doc open past the TTL and
 * then seeks an unbuffered video chunk, or clicks a download link —
 * is handled by the consumer's `<video onError>` / `<a onError>` path,
 * which calls `refresh()` to mint a new URL and re-attach.
 *
 * No `refetchInterval` here by design. A recurring poll while the tab
 * is open generates a steady BE heartbeat (one query per embed per
 * interval) that prevents Aurora SV2 staging from auto-pausing and
 * holds RDS Proxy connection slots warm. The error-recovery path covers
 * the same UX surface with zero steady-state cost.
 *
 * The BE's `/api/uploads/:id/url?as=json` returns the URL string under
 * `data.url`; consumers wrap their `<img/video/etc src>` with the
 * resolved value once `data` is non-null.
 *
 * Errors surface as TanStack Query's `error`; callers can read
 * `isError` and render a localized fallback (per the i18n catalog
 * `errors.*` map and `localizeError` helper). 403 → consumer renders
 * `[Restricted]`-style chrome; 404 → consumer renders `[Deleted]`.
 *
 * Disabled when `attachmentId` is empty/undefined to keep the call
 * shape ergonomic during loading or placeholder states (an embed that
 * hasn't been wired yet has no fileId).
 */

import { useQuery } from '@tanstack/react-query'

import { apiClient } from '@/api/client'

/** Stable cache key — one entry per attachment id. */
export const FILE_PRESIGNED_URL_QUERY_KEY = (attachmentId: string) =>
  ['file-presigned-url', attachmentId] as const

/**
 * BE response shape for `GET /api/uploads/:id/url?as=json`. Mirrors the
 * `SignedGetResponse` used by the upload hooks but lives here so the
 * embed NodeViews don't reach into the uploads feature for a type.
 */
interface SignedGetResponse {
  data: {
    url: string
    contentType: string
    originalFilename: string
  }
}

export interface UseFilePresignedUrlResult {
  /** The presigned GET URL, ready to drop into `<video src>` / `<a href>`. */
  url: string | undefined
  isLoading: boolean
  isError: boolean
  /**
   * HTTP status from the underlying `ApiError` when the request failed.
   * Lets the NodeView render the canonical `[Restricted]` (403) or
   * `[Deleted]` (404) fallback without re-reading the error envelope.
   */
  status: number | null
  /** Imperative refresh — call from `<video onError>` recovery paths. */
  refresh: () => Promise<void>
}

export function useFilePresignedUrl(
  attachmentId: string | null | undefined,
): UseFilePresignedUrlResult {
  const query = useQuery({
    queryKey: FILE_PRESIGNED_URL_QUERY_KEY(attachmentId ?? ''),
    queryFn: async () => {
      const res = await apiClient<SignedGetResponse>(`/api/uploads/${attachmentId}/url?as=json`)
      return res.data
    },
    enabled: Boolean(attachmentId),
    // Hold the minted URL for the lifetime of the mount. A tab-flip
    // mid-playback must NOT yank a new URL into a playing `<video>` and
    // we don't want a steady BE heartbeat per embed; the consumer's
    // `onError → refresh()` path covers the rare post-TTL miss.
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    retry: false,
  })

  const errorObj = query.error
  const status =
    errorObj && typeof errorObj === 'object' && 'status' in errorObj
      ? (((errorObj as { status: unknown }).status as number | null) ?? null)
      : null

  return {
    url: query.data?.url,
    isLoading: query.isLoading,
    isError: query.isError,
    status,
    refresh: async () => {
      await query.refetch()
    },
  }
}
